/* eslint-disable @typescript-eslint/no-explicit-any */
// 이 파일은 asset 내의 interface.js 를
// typescript / react 에서 사용하기 좋게 감싸거나
// helper function 을 제공한다.

// base64

import { BACK_AFTER_EDITING } from 'constants/frontend/shared/values';
import {
  ActionSheetActionT,
  DeviceInfoKeyT,
  OsVerInfoT,
} from 'types/frontend/inapp';
import { NativeDataKeyT } from 'types/frontend/shared';
import { TokenObjT } from 'types/shared/core/Token';

type ButtonPosition = 'left' | 'rightMain' | 'rightSub';

type IconName =
  | 'back'
  | 'chat'
  | 'close'
  | 'search'
  | 'settings'
  | 'notifications'
  | 'more';

type ThemeMode = 'light' | 'dark';
type ThemeSetting = ThemeMode | 'system';

export type PermissionRequestResultT =
  | 'granted'
  | 'denied'
  | 'userActionRequired'
  | 'unavailable'
  | 'error';

export type AudioRecordCommandT = 'start' | 'stop' | 'cancel';

export type AudioRecordNativeEventT =
  | {
      status: 'recording';
      elapsedMs: number;
      gain: number;
    }
  | {
      status: 'finalizing';
      elapsedMs: number;
    }
  | {
      status: 'readyToUpload';
      recordingId: string;
      elapsedMs: number;
      mimeType: string;
      byteSize: number;
      chunkSize: number;
      chunkCount: number;
    }
  | {
      status: 'recordingFailed';
      reason?: string;
    };

export type RecordedAudioChunkResultT = {
  recordingId: string;
  chunkIndex: number;
  byteLength: number;
  base64?: string;
  errorMessage?: string;
};

type NativeEventHandlerT = (eventType: string, eventData: any) => void;

let primaryNativeEventHandler: NativeEventHandlerT | undefined;
const additionalNativeEventHandlers = new Set<NativeEventHandlerT>();

const dispatchNativeEvent: NativeEventHandlerT = (eventType, eventData) => {
  primaryNativeEventHandler?.(eventType, eventData);
  additionalNativeEventHandlers.forEach((handler) => {
    handler(eventType, eventData);
  });
};

// | 'write'
// | 'news'
// | 'note'
// | 'schedule'
// | 'more';

//
// main
function requestNativeFeature(
  obj: any,
  resultHandler?: any,
  shouldReturn?: boolean,
) {
  // console.log('interfaceBridge - requestNativeFeature', obj);
  return (window as any).requestNativeFeature(obj, resultHandler, shouldReturn);
}

//
//
// navigation bar
function setTitle(title: string) {
  requestNativeFeature({
    action: 'title',
    title,
  });
}

function setButtonText(position: 'rightMain' | 'rightSub', title?: string) {
  requestNativeFeature({
    action: 'button',
    position,
    title,
  });
}

function setButtonIcon(position: ButtonPosition, icon?: IconName) {
  requestNativeFeature({
    action: 'button',
    position,
    icon,
  });
}

function showNavigationBar(isVisible: boolean, withAnimation: boolean) {
  (window as any).showNavigationBar(isVisible, withAnimation);
}

//
//
// navigation
function back(result?: string) {
  console.log('interfaceBridge back, result=', result);
  requestNativeFeature({
    action: 'back',
    result,
  });
}

function backAfterEditing() {
  requestNativeFeature({
    action: 'back',
    result: BACK_AFTER_EDITING,
  });
}

function nav(
  initialTitle: string,
  url: string,
  target: 'current' | 'cover' | 'external',
  needResult = false,
  resultHandler: ((resultObj: any) => void) | undefined = undefined,
) {
  interfaceBridge.requestNativeFeature(
    {
      action: 'nav',
      initialTitle,
      url,
      target,
      needResult,
    },
    resultHandler,
    false,
  );
}

// 채팅에서 왼쪽에 창 띄우기용
function navOnLeftSide(
  initialTitle: string,
  url: string,
  target: 'current' | 'cover' | 'external',
  needResult = false,
  resultHandler: ((resultObj: any) => void) | undefined = undefined,
) {
  interfaceBridge.requestNativeFeature(
    {
      action: 'nav',
      initialTitle,
      url,
      target,
      needResult,
      isOnLeftSide: true,
    },
    resultHandler,
    false,
  );
}

//
// 학생측 홈 화면 띄우기
// nav와 다르게 화면 스택을 모두 없앰
function navStudentSideHome() {
  interfaceBridge.requestNativeFeature({
    action: 'navStudentSideHome',
  });
}

//
// 학생측 탭있는 화면 띄우기
// 한 학생은 여러 학원에 다니므로 academyId가 필요
function navStudentSide(academyId: number, academyName: string) {
  interfaceBridge.requestNativeFeature({
    action: 'navStudentSide',
    academyId,
    academyName,
  });
}
//
// 교사측 탭있는 화면 띄우기
function navTeacherSide() {
  interfaceBridge.requestNativeFeature({
    action: 'navTeacherSide',
  });
}

//
//
// ui snippets
function customAlert(title: string, message: string, closeBtn: string): void {
  (window as any).customAlert(title, message, closeBtn);
}

function customConfirm(
  title: string,
  message: string,
  confirmBtn: string,
  cancelBtn: string,
  isDestructive?: boolean,
): boolean {
  return (window as any).customConfirm(
    title,
    message,
    confirmBtn,
    cancelBtn,
    isDestructive,
  );
}

function setBtnEventHandler(
  handler: Partial<Record<ButtonPosition, () => void>>,
): void {
  if (handler.left) (window as any).btnEventHandlerLeft = handler.left;
  if (handler.rightMain)
    (window as any).btnEventHandlerRightMain = handler.rightMain;
  if (handler.rightSub)
    (window as any).btnEventHandlerRightSub = handler.rightSub;
}

function setNativeEventHandler(handler: NativeEventHandlerT) {
  primaryNativeEventHandler = handler;
  (window as any).nativeEventHandler = dispatchNativeEvent;
  // console.log(
  //   'window.nativeEventHandler = ',
  //   (window as any).nativeEventHandler,
  // );
}

function addNativeEventHandler(handler: NativeEventHandlerT) {
  additionalNativeEventHandlers.add(handler);
  (window as any).nativeEventHandler = dispatchNativeEvent;

  return () => {
    additionalNativeEventHandlers.delete(handler);
  };
}

//
//
// token
// saveToken이후 할 작업이 있으므로 동기적으로 return 필요
// null 을 주면 삭제된다.
function saveToken(userId: number, tokenObj: TokenObjT | null): string {
  const result = interfaceBridge.requestNativeFeature(
    {
      action: 'saveToken',
      userId,
      tokenObj,
    },
    null,
    true,
  );

  console.log('save token result = ', result);
  return 'success';
}

function loadToken(userId: number): TokenObjT | null {
  const result = interfaceBridge.requestNativeFeature(
    {
      action: 'loadToken',
      userId,
    },
    null,
    true,
  );

  if (!result) return null;

  return JSON.parse(result) as TokenObjT | null;
}

function setData(key: NativeDataKeyT, dataStr: string): void {
  interfaceBridge.requestNativeFeature(
    {
      action: 'setData',
      key,
      valueStr: dataStr,
    },
    null,
    true,
  );
  // console.log('setData', dataStr);
}

function getData(key: NativeDataKeyT): string {
  const result = interfaceBridge.requestNativeFeature(
    {
      action: 'getData',
      key,
    },
    null,
    true,
  );
  // console.log('getData', result);
  return result as string;
}

function deviceInfo(key: DeviceInfoKeyT): string {
  const result = interfaceBridge.requestNativeFeature(
    {
      action: 'deviceInfo',
      key,
    },
    null,
    true,
  );
  // console.log('deviceInfo', result);
  return (result as string) || '';
}

function getOsVer(): OsVerInfoT | null {
  const result = deviceInfo('osVer');

  try {
    const resultObj = JSON.parse(result);
    const app = resultObj?.app;
    const ver = Number(resultObj?.ver);

    if ((app !== 'android' && app !== 'ios') || !Number.isFinite(ver)) {
      return null;
    }

    const info: OsVerInfoT = {
      app,
      ver,
    };

    if (app === 'ios' && typeof resultObj?.full === 'string') {
      info.full = resultObj.full;
    }

    if (app === 'android' && Number.isFinite(Number(resultObj?.sdk))) {
      info.sdk = Number(resultObj.sdk);
    }

    return info;
  } catch {
    return null;
  }
}

function getUserIds(): number[] {
  const result = interfaceBridge.requestNativeFeature(
    {
      action: 'getUserIds',
    },
    null,
    true,
  );

  return JSON.parse(result) as number[];
}

function getAppVersionName(): string {
  try {
    const result = interfaceBridge.requestNativeFeature(
      {
        action: 'getAppVersion',
      },
      null,
      true,
    );

    const resultObj = JSON.parse(result);
    return resultObj.name || '';
  } catch (e) {
    return '';
  }
}

function getAppBuildNumber(): number {
  try {
    const result = interfaceBridge.requestNativeFeature(
      {
        action: 'getAppVersion',
      },
      null,
      true,
    );

    console.log('getAppVersion result=', result);

    const resultObj = JSON.parse(result);
    return resultObj.build || 0;
  } catch {
    return 0;
  }
}

function getTheme(): ThemeMode | '' {
  const result = interfaceBridge.requestNativeFeature(
    {
      action: 'getTheme',
    },
    null,
    true,
  );

  return (result as ThemeMode | '') || '';
}

function setThemeSetting(themeSetting: ThemeSetting): void {
  interfaceBridge.requestNativeFeature(
    {
      action: 'setThemeSetting',
      themeSetting,
    },
    null,
    true,
  );
}

function getThemeSetting(): ThemeSetting {
  const result = interfaceBridge.requestNativeFeature(
    {
      action: 'getThemeSetting',
    },
    null,
    true,
  );

  const setting = (result as ThemeSetting | '') || '';
  return setting === 'light' || setting === 'dark' || setting === 'system'
    ? setting
    : 'system';
}

function isTestEnvironment(): boolean {
  return !!(window as any).isTestEnvironment;
}

// part 7 - 이 함수는 토큰 관련이지만 비동기적, 리턴 필요 없음
function sendPushTokenToServer() {
  interfaceBridge.requestNativeFeature({
    action: 'sendPushTokenToServer',
  });
}

//
//
//

function uploadContentImage(
  academyId: number,
  contentIdentifier: string,
  maxCount: number,
) {
  interfaceBridge.requestNativeFeature({
    action: 'uploadContentImage',
    academyId,
    contentIdentifier,
    maxCount,
  });
}

function uploadContentVideo(
  academyId: number,
  contentIdentifier: string,
  maxCount: number,
) {
  interfaceBridge.requestNativeFeature({
    action: 'uploadContentVideo',
    academyId,
    contentIdentifier,
    maxCount,
  });
}

function uploadContentAttachment(
  academyId: number,
  contentIdentifier: string,
  maxCount: number,
) {
  interfaceBridge.requestNativeFeature({
    action: 'uploadContentAttachment',
    academyId,
    contentIdentifier,
    maxCount,
  });
}

function uploadContentAudio(
  academyId: number,
  contentIdentifier: string,
  fileKey: string,
) {
  interfaceBridge.requestNativeFeature({
    action: 'uploadContentAudio',
    academyId,
    contentIdentifier,
    fileKey,
  });
}

function cancelUpload(fileKey: string) {
  interfaceBridge.requestNativeFeature({
    action: 'cancelUpload',
    fileKey,
  });
}

function uploadTempImage(
  academyId: number,
  type: 'studentProfile' | 'userProfile' | 'corpReg',
  maxWidth: number,
  ratioW: number, // 0 이면 비율 제한 없음 - crop 창 뜨지 않음
  ratioH: number,
  resultHandler: (resultObj: any) => void,
) {
  interfaceBridge.requestNativeFeature(
    {
      action: 'uploadTempImage',
      academyId,
      type,
      maxWidth,
      ratioW,
      ratioH,
    },
    resultHandler,
    false,
  );
}

function downloadFile(
  url: string,
  fileName: string,
  type: 'image' | 'video' | 'etc',
) {
  interfaceBridge.requestNativeFeature({
    action: 'downloadFile',
    url,
    fileName,
    type,
  });
}

function toast(
  category: 'info' | 'warning' | 'error',
  title: string,
  message: string,
) {
  interfaceBridge.requestNativeFeature({
    action: 'toast',
    category: category, // info, warning, error
    title: title,
    message: message,
  });
}

function actionSheet(
  title: string,
  message: string | undefined, // undefined를 전해서 생략 가능
  actions: ActionSheetActionT[],
  resultHandler: (value: string) => void,
) {
  interfaceBridge.requestNativeFeature(
    {
      action: 'actionSheet',
      title: title,
      message: message,
      actions: actions,
    },
    resultHandler,
  );
}

//
//
// part 8 - native 자원 활용 기능
function requestPermission(
  permissionKey: 'AUDIO_RECORD' | 'WRITE_EXTERNAL_STORAGE',
  resultHandler: (result: PermissionRequestResultT) => void,
) {
  console.log(
    `[AudioRecordPermission][bridge] request: permissionKey=${permissionKey}`,
  );

  const sanitizeResult = (result: unknown): PermissionRequestResultT => {
    switch (result) {
      case 'granted':
      case 'denied':
      case 'userActionRequired':
      case 'unavailable':
      case 'error':
        return result;
      default:
        console.error(
          `[AudioRecordPermission][bridge] invalid response: permissionKey=${permissionKey}, result=${String(
            result,
          )}`,
        );
        return 'error';
    }
  };

  requestNativeFeature(
    {
      action: 'requestPermission',
      permissionKey,
    },
    (rawResult: unknown) => {
      const result = sanitizeResult(rawResult);
      console.log(
        `[AudioRecordPermission][bridge] response: permissionKey=${permissionKey}, rawResult=${String(
          rawResult,
        )}, result=${result}`,
      );
      resultHandler(result);
    },
    false,
  );
}

function audioRecord(command: AudioRecordCommandT) {
  requestNativeFeature({
    action: 'audioRecord',
    command,
    maxDurationMs: 10 * 60 * 1000,
  });
}

// 기존 비동기 result 계약이 응답 문자열 전체를 Base64로 포장하므로
// 청크의 base64 필드는 브리지를 지나는 동안 한 번 더 인코딩된다.
function getRecordedAudioChunk(
  recordingId: string,
  chunkIndex: number,
  resultHandler: (result: RecordedAudioChunkResultT) => void,
) {
  requestNativeFeature(
    {
      action: 'getRecordedAudioChunk',
      recordingId,
      chunkIndex,
    },
    (rawResult: unknown) => {
      if (typeof rawResult !== 'string') {
        resultHandler({
          recordingId,
          chunkIndex,
          byteLength: 0,
          errorMessage: 'invalidResponse',
        });
        return;
      }

      try {
        const result = JSON.parse(rawResult) as Partial<RecordedAudioChunkResultT>;
        if (
          result.recordingId !== recordingId ||
          result.chunkIndex !== chunkIndex ||
          !Number.isInteger(result.byteLength) ||
          Number(result.byteLength) < 0
        ) {
          throw new Error('invalid audio chunk response');
        }

        resultHandler(result as RecordedAudioChunkResultT);
      } catch {
        resultHandler({
          recordingId,
          chunkIndex,
          byteLength: 0,
          errorMessage: 'invalidResponse',
        });
      }
    },
    false,
  );
}

type TranslationDirection = 'korToEng' | 'engToKor';
type TranslationResultHandler = (value: string, errorMessage?: string) => void;

interface NativeTranslationResult {
  value: string;
  errorMessage?: string;
}

function handleTranslationResult(
  rawResult: string | null,
  resultHandler: TranslationResultHandler,
) {
  if (!rawResult) {
    resultHandler('', '번역 결과를 받지 못했습니다. 다시 시도해 주세요.');
    return;
  }

  try {
    const result = JSON.parse(rawResult) as NativeTranslationResult;
    if (typeof result.value !== 'string') {
      throw new Error('invalid translation result');
    }
    resultHandler(result.value, result.errorMessage);
  } catch (error) {
    resultHandler('', '번역 결과를 처리하지 못했습니다. 다시 시도해 주세요.');
  }
}

function prepareTranslation(
  direction: TranslationDirection,
  resultHandler: TranslationResultHandler,
) {
  interfaceBridge.requestNativeFeature(
    {
      action: 'translate',
      command: 'prepare',
      direction,
    },
    (rawResult: string | null) =>
      handleTranslationResult(rawResult, resultHandler),
  );
}

function translate(
  text: string,
  direction: TranslationDirection,
  resultHandler: TranslationResultHandler,
) {
  interfaceBridge.requestNativeFeature(
    {
      action: 'translate',
      text,
      direction,
    },
    (rawResult: string | null) =>
      handleTranslationResult(rawResult, resultHandler),
  );
}

const interfaceBridge = {
  requestNativeFeature,
  // part 1 - 네비게이션 관련
  back,
  backAfterEditing,
  nav,
  navOnLeftSide,
  navStudentSideHome,
  navStudentSide,
  navTeacherSide,
  // part 2 - ui snippets
  toast,
  actionSheet,
  // part 3 - navigationbar 설정관련
  setTitle,
  setBtnEventHandler,
  showNavigationBar,
  setButtonText,
  setButtonIcon,
  setNativeEventHandler,
  addNativeEventHandler,
  // part 4 - javascript 기본 기능 확장
  customAlert,
  customConfirm,
  // part 5 perference
  saveToken,
  loadToken,
  getUserIds,
  getData,
  setData,
  deviceInfo,
  getOsVer,
  getAppVersionName,
  getAppBuildNumber,
  setThemeSetting,
  getThemeSetting,
  getTheme,
  isTestEnvironment,
  // part 6 - native 화면 필요한 부가기능
  uploadContentImage,
  uploadContentVideo,
  uploadContentAttachment,
  uploadContentAudio,
  uploadTempImage,
  cancelUpload,
  downloadFile,
  // part 7 - 토큰 서버에 보내기
  sendPushTokenToServer,
  // part 8 - native 자원 활용 기능
  requestPermission,
  audioRecord,
  getRecordedAudioChunk,
  prepareTranslation,
  translate,
};

export default interfaceBridge;
